"use strict";
/**
 * InkStep Assessment shared types — entry & periodic phonics assessment.
 *
 * WP-068: Pseudo-word assessment items and attainedSequencePosition algorithm.
 * These types model the assessment of a student's phonics decoding ability
 * using pseudo-words (nonsense words) that prevent sight-vocabulary guessing.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeAttainedSequencePosition = computeAttainedSequencePosition;
/**
 * Compute the attained sequence position using the furthest-contiguous
 * algorithm.
 *
 * The algorithm: group items by targetPosition, iterate positions in ascending
 * order, and return the last position where ALL items at that position were
 * correct. Stop at the first position where any item is incorrect — the
 * student cannot "skip ahead" past a gap in phonics knowledge.
 *
 * @param results The item results from an assessment.
 * @returns The attained sequence position (0 if all incorrect or empty).
 */
function computeAttainedSequencePosition(results) {
    if (!results || results.length === 0) {
        return { attainedPosition: 0, totalItems: 0, correctCount: 0 };
    }
    // Group items by targetPosition
    const positionMap = new Map();
    for (const item of results) {
        const existing = positionMap.get(item.targetPosition) || [];
        existing.push(item.isCorrect);
        positionMap.set(item.targetPosition, existing);
    }
    // Sort positions ascending
    const sortedPositions = Array.from(positionMap.keys()).sort((a, b) => a - b);
    // The furthest-contiguous algorithm: iterate positions starting from the
    // minimum, checking that each position is exactly previous+1 (contiguous)
    // and that ALL items at that position are correct. Stop at the first gap
    // or failure.
    let attained = 0;
    let expectedPosition = sortedPositions[0];
    for (const position of sortedPositions) {
        // Check contiguity: position must equal the expected next position
        if (position !== expectedPosition) {
            break;
        }
        const itemsAtPosition = positionMap.get(position);
        const allCorrect = itemsAtPosition.every((c) => c === true);
        if (allCorrect) {
            attained = position;
            expectedPosition = position + 1;
        }
        else {
            // First failure stops the contiguous chain
            break;
        }
    }
    const correctCount = results.filter((r) => r.isCorrect).length;
    return {
        attainedPosition: attained,
        totalItems: results.length,
        correctCount,
    };
}
